import { z } from 'zod'
import { tagsSchema } from './items'
import { defaultRules, mergeRules, rulesPatchSchema, userRulesSchema } from './rules'
import { speechLanguageSchema } from './speech'

/** True when the runtime knows this IANA time zone. */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM (24h)')

/** One working window on a day, e.g. ["09:00", "18:00"]. */
export const workWindowSchema = z
  .tuple([hhmm, hhmm])
  .refine(([start, end]) => start < end, 'window must end after it starts')

export const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type WeekdayKey = (typeof weekdayKeys)[number]

const dayWindows = z.array(workWindowSchema).max(4)

export const workingHoursSchema = z.object({
  mon: dayWindows,
  tue: dayWindows,
  wed: dayWindows,
  thu: dayWindows,
  fri: dayWindows,
  sat: dayWindows,
  sun: dayWindows,
})
export type WorkingHours = z.output<typeof workingHoursSchema>

/** Holiday calendars hugo knows. `none` means only weekends are off. */
export const holidayRegions = [
  'none',
  'CH',
  'CH-ZH',
  'CH-BE',
  'CH-BS',
  'CH-GE',
  'CH-VD',
  'CH-LU',
  'CH-SG',
  'CH-AG',
  'CH-TI',
  'DE',
  'AT',
] as const
export const holidayRegionSchema = z.enum(holidayRegions)
export type HolidayRegion = z.output<typeof holidayRegionSchema>

/** Planning capacity: minutes of a working day hugo plans for dated tasks and promises. */
export const PLANNING_MINUTES_MIN = 30
export const PLANNING_MINUTES_MAX = 720
export const PLANNING_MINUTES_STEP = 30

export const userSettingsSchema = z.object({
  timezone: z.string().refine(isValidTimeZone, 'unknown time zone'),
  /** 1 = Monday, 0 = Sunday. */
  weekStart: z.union([z.literal(1), z.literal(0)]),
  workingHours: workingHoursSchema,
  holidayRegion: holidayRegionSchema,
  /**
   * hugo files an inbox item itself (undoably) when its AI is sure (high confidence) that it is
   * a task, I owe or waiting on. FYI and ignore are never applied by themselves. No `.default()`
   * here: the patch schema below would then switch it back on with every other change.
   */
  aiAutoFile: z.boolean(),
  /**
   * The part of a working day hugo plans for dated tasks and promises (30–720 minutes in steps
   * of 30); a day's capacity is at most its working minutes. No `.default()`, like aiAutoFile.
   */
  planningMinutesPerDay: z
    .number()
    .int()
    .min(PLANNING_MINUTES_MIN)
    .max(PLANNING_MINUTES_MAX)
    .multipleOf(PLANNING_MINUTES_STEP),
  /** The language the user speaks when dictating (the speech service needs it). No default. */
  speechLanguage: speechLanguageSchema,
  /**
   * Tags whose items are personal (family, house): hugo keeps them out of the working hours it
   * plans (decision 97). No default, like aiAutoFile.
   */
  personalTags: tagsSchema,
  /** The numbers hugo decides with (see rules.ts); each rule defaults to what hugo did before. */
  rules: userRulesSchema,
})
export type UserSettings = z.output<typeof userSettingsSchema>

const office: [string, string][] = [['09:00', '18:00']]

export const defaultUserSettings: UserSettings = {
  timezone: 'Europe/Zurich',
  weekStart: 1,
  workingHours: {
    mon: office,
    tue: office,
    wed: office,
    thu: office,
    fri: office,
    sat: [],
    sun: [],
  },
  holidayRegion: 'CH-ZH',
  aiAutoFile: true,
  planningMinutesPerDay: 240,
  speechLanguage: 'en',
  personalTags: [],
  rules: defaultRules,
}

/** A change to the settings: the fields it names; for rules, only the rules it names. */
export const userSettingsPatchSchema = userSettingsSchema
  .extend({ rules: rulesPatchSchema })
  .partial()
export type UserSettingsPatch = z.output<typeof userSettingsPatchSchema>

/** The settings after a change (validated); rules merge rule by rule (see mergeRules). */
export function applySettingsPatch(current: UserSettings, patch: UserSettingsPatch): UserSettings {
  const { rules, ...rest } = patch
  return userSettingsSchema.parse({
    ...current,
    ...rest,
    rules: rules ? mergeRules(current.rules, rules) : current.rules,
  })
}
